import { Typography } from '@mui/material';
import { RiDeleteBinLine, RiHome3Line, RiMore2Line, RiPencilLine } from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import BoldHeader from 'src/components/atoms/grid/BoldHeader';
import { AgButton, AgSwitch } from 'src/components/ui';
import { AgDropdownItem, AgDropdownMenu } from 'src/components/ui/AgDropdownMenu';
import { GridColObj } from 'src/modules/grids';
import { appLinks } from 'src/routing/appLinks';
import formatAddress from 'src/utils/address/formatAddress';
import { Place } from 'types/graphql';
import { ADDRESS_ROLE_MAP } from '../../businessEntity/constants';
import { OrgPlace } from '../myAddresses.api';

type AddressRole = 'BILLING' | 'SHIPPING' | 'WAREHOUSE' | 'PRIMARY_BUSINESS' | 'SUBSIDIARY' | 'OTHER';

type UseMyAddressColumnsProps = {
  onEdit: (address: OrgPlace) => void;
  onToggleDisabled: (address: OrgPlace) => void;
  onDelete: (addressId: number) => void;
};

export function useMyAddressColumns({
  onEdit,
  onToggleDisabled,
  onDelete,
}: UseMyAddressColumnsProps): GridColObj<OrgPlace> {
  const navigate = useNavigate();

  return useMemo(
    (): GridColObj<OrgPlace> => ({
      description: {
        headerName: 'Name',
        minWidth: 150,
        flex: 1,
        type: 'string',
        filterable: false,
        renderHeader: BoldHeader,
        renderCell: ({ row }) => (
          <Typography style={{ textDecoration: row.disabled ? 'line-through' : 'none' }} sx={{ fontSize: '14px' }}>
            {row.description}
          </Typography>
        ),
      },
      address: {
        headerName: 'Address',
        minWidth: 150,
        flex: 2.5,
        type: 'string',
        filterable: false,
        // Formatted from several columns, so the server cannot sort it (TAS-3660 P7 BZ76).
        sortable: false,
        renderHeader: BoldHeader,
        // No `address` field on the row; exports read this value, not the rendered cell.
        valueGetter: (_value, row) => formatAddress(row as Place),
        renderCell: ({ row }) => (
          <Typography style={{ textDecoration: row.disabled ? 'line-through' : 'none' }} sx={{ fontSize: '14px' }}>
            {formatAddress(row as Place)}
          </Typography>
        ),
      },
      addressRoles: {
        headerName: 'Role',
        minWidth: 180,
        flex: 1.5,
        type: 'string',
        // A list column: the server filters it (hasSome) but cannot sort it (TAS-3660 P7 BZ76).
        sortable: false,
        renderHeader: BoldHeader,
        customFilterType: 'string_array',
        getFilterSelectOptions: Object.entries(ADDRESS_ROLE_MAP).map(([value, label]) => ({ value, label })),
        renderCell: ({ value }) => (
          <div className="flex gap-1 flex-wrap">
            {value?.map((role: AddressRole) => (
              <span className="badge" key={role}>
                {ADDRESS_ROLE_MAP[role]}
              </span>
            ))}
          </div>
        ),
      },
      phone: {
        headerName: 'Phone',
        minWidth: 150,
        flex: 1,
        type: 'string',
        filterable: false,
        renderHeader: BoldHeader,
      },
      actions: {
        type: 'actions',
        headerName: '',
        minWidth: 140,
        disableExport: true,
        renderCell: ({ row: address }) => (
          <div className="flex w-full items-center justify-end gap-1">
            {address.isActive && (
              <AgSwitch size="sm" isSelected={!address.disabled} onChange={() => onToggleDisabled(address)} />
            )}
            <AgButton variant="ghost" size="icon-sm" shape="circle" excludeFromTabOrder onPress={() => onEdit(address)}>
              <RiPencilLine size={20} />
            </AgButton>
            <AgDropdownMenu
              size="icon-sm"
              icon={<RiMore2Line size={20} />}
              variant="ghost"
              excludeFromTabOrder
              items={
                <>
                  {!address.isActive && (
                    <AgDropdownItem
                      icon={<RiDeleteBinLine size={20} />}
                      destructive
                      onAction={() => onDelete(address.id)}
                    >
                      Delete
                    </AgDropdownItem>
                  )}
                  {address.warehouse?.id && (
                    <AgDropdownItem
                      icon={<RiHome3Line size={20} />}
                      onAction={() => void navigate(appLinks.warehouseDetails({ warehouseId: address.warehouse.id }))}
                    >
                      Go to Warehouse
                    </AgDropdownItem>
                  )}
                </>
              }
            />
          </div>
        ),
      },
    }),
    [onEdit, onToggleDisabled, onDelete, navigate]
  );
}
